import { useState, useEffect } from 'react';
import { publicApi } from '@/lib/api';
import { FadeIn } from '@/components/FadeIn';
import { useContentValue } from '@/contexts/SiteContentContext';
import { Target, Gamepad2, Loader2 } from 'lucide-react';

const ICONS = { Darts: Target, 'Interaktiver Spieltisch': Gamepad2 };
const DESCRIPTIONS = {
  Darts: 'Fordere deine Freunde zu einer Runde Darts heraus. Unser elektronischer Dartsautomat sorgt für spannende Matches in entspannter Lounge-Atmosphäre.',
  'Interaktiver Spieltisch': 'Unser interaktiver Spieltisch bietet eine Vielzahl an Spielen für Jung und Alt. Perfekt für gesellige Abende und spontane Unterhaltung.',
};

export default function EntertainmentSection() {
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const c = useContentValue();

  useEffect(() => {
    publicApi.getImages('entertainment').then((res) => setImages(res.data.images || []))
      .catch(() => {}).finally(() => setLoading(false));
  }, []);

  return (
    <section data-testid="entertainment-section" className="py-24 lg:py-32 relative">
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-[#1C1C1B]/30 to-transparent pointer-events-none" />
      <div className="relative max-w-7xl mx-auto px-6">
        <FadeIn>
          <div className="text-center mb-16">
            <span className="text-amber-400 text-xs tracking-[0.3em] uppercase font-body font-semibold">{c('Spiel.Eyebrow', 'Freizeit')}</span>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-extrabold text-stone-100 mt-3 tracking-wide uppercase">{c('Spiel.Überschrift', 'Spiel & Unterhaltung')}</h2>
            <p className="text-stone-400 text-base max-w-xl mx-auto mt-4 font-body leading-relaxed">
              {c('Spiel.Beschreibung', 'Neben Kaffee, Cocktails und Events bietet das TreffPunkt auch entspannte Unterhaltung — für gesellige Momente unter Freunden.')}
            </p>
          </div>
        </FadeIn>
        {loading ? (
          <div className="flex justify-center py-16"><Loader2 size={32} className="text-amber-400 animate-spin" /></div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-8">
            {images.map((img, i) => {
              const Icon = ICONS[img.name] || Target;
              const desc = DESCRIPTIONS[img.name] || '';
              return (
                <FadeIn key={img.id || i} delay={i * 150}>
                  <div data-testid={`entertainment-${img.name.toLowerCase().replace(/\s+/g, '-')}`} className="group glass-card rounded-2xl overflow-hidden hover:border-amber-400/20 transition-all duration-500">
                    <div className="h-[320px] sm:h-[380px] overflow-hidden relative">
                      <img src={img.image_url} alt={`${img.name} – TreffPunkt Café-Bar Luzern`} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                      <div className="absolute inset-0 bg-gradient-to-t from-[#1C1C1B] via-transparent to-transparent" />
                    </div>
                    <div className="p-6 sm:p-8 -mt-16 relative">
                      <div className="flex items-center gap-3 mb-3">
                        <div className="w-11 h-11 rounded-full bg-amber-500/10 border border-amber-400/20 flex items-center justify-center">
                          <Icon size={20} className="text-amber-400" />
                        </div>
                        <h3 className="font-display text-xl font-bold text-stone-100 tracking-wide uppercase">{img.name}</h3>
                      </div>
                      <p className="text-stone-400 text-sm font-body leading-relaxed">{desc}</p>
                    </div>
                  </div>
                </FadeIn>
              );
            })}
          </div>
        )}
      </div>
    </section>
  );
}
